import React, { useState, useEffect, useCallback } from "react";
import { fetchWithAuth } from "../../utils/fetchWithAuth";

const API = process.env.REACT_APP_API_URL || "http://localhost:5000";

const FORMATS     = ["pdf", "excel"];
const FREQUENCIES = ["daily", "weekly", "monthly"];
const SECTIONS    = ["overview", "platforms", "social", "audience", "trending"];

const EMPTY_FORM = { name: "", format: "pdf", frequency: "weekly", recipients: "", sections: ["overview"] };

export function ReportsPage({ theme: t, isMobile, user }) {
  const [schedules, setSchedules] = useState([]);
  const [history,   setHistory]   = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [tab,       setTab]       = useState("schedules");
  const [msg,       setMsg]       = useState("");
  const [showForm,  setShowForm]  = useState(false);
  const [form,      setForm]      = useState(EMPTY_FORM);
  const [saving,    setSaving]    = useState(false);
  const [formErr,   setFormErr]   = useState("");

  const isAdmin = user?.role === "super_admin" || user?.role === "admin";

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [sr, hr] = await Promise.all([
        fetchWithAuth(`${API}/api/reports/schedule`),
        fetchWithAuth(`${API}/api/reports/history`),
      ]);
      if (sr.ok) setSchedules(await sr.json());
      if (hr.ok) setHistory(await hr.json());
    } catch {}
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  async function handleSave() {
    if (!form.name.trim()) { setFormErr("Name is required"); return; }
    const recs = form.recipients.split(",").map(r => r.trim()).filter(Boolean);
    if (!recs.length) { setFormErr("At least one recipient is required"); return; }
    setSaving(true); setFormErr("");
    try {
      const res = await fetchWithAuth(`${API}/api/reports/schedule`, {
        method: "POST",
        body: JSON.stringify({ ...form, recipients: recs }),
      });
      const d = await res.json();
      if (!res.ok) { setFormErr(d.error || "Failed to create schedule"); setSaving(false); return; }
      setMsg("✓ Schedule created.");
      setShowForm(false);
      setForm(EMPTY_FORM);
      load();
    } catch { setFormErr("Network error"); }
    setSaving(false);
  }

  async function handleDelete(id) {
    if (!window.confirm("Delete this schedule?")) return;
    try {
      const res = await fetchWithAuth(`${API}/api/reports/schedule/${id}`, { method: "DELETE" });
      if (!res.ok) throw new Error((await res.json()).error);
      setMsg("✓ Schedule deleted.");
      load();
    } catch (e) { setMsg(`✗ ${e.message}`); }
  }

  async function handleRun(id) {
    try {
      const res = await fetchWithAuth(`${API}/api/reports/run/${id}`, { method: "POST" });
      if (!res.ok) throw new Error((await res.json()).error);
      setMsg("✓ Report queued. Check History in a moment.");
    } catch (e) { setMsg(`✗ ${e.message}`); }
  }

  const STATUS_COLORS = { success: "#22c55e", failed: "#DC2626", running: "#f59e0b" };

  return (
    <div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 20, flexWrap: "wrap", gap: 12 }}>
        <div>
          <h3 style={{ margin: 0, fontSize: 18, fontWeight: 900, color: t.text, fontFamily: "Georgia, serif" }}>Scheduled Reports</h3>
          <p style={{ margin: "4px 0 0", fontSize: 12, color: t.textMuted }}>Automated PDF/Excel reports sent to your inbox</p>
        </div>
        {isAdmin && (
          <button onClick={() => { setShowForm(s => !s); setFormErr(""); }} style={{ background: "#2563EB", border: "none", borderRadius: 10, padding: "9px 18px", color: "#fff", fontSize: 12, fontWeight: 800, cursor: "pointer", fontFamily: "Georgia, serif" }}>
            + New Schedule
          </button>
        )}
      </div>

      {msg && (
        <div style={{ background: msg.startsWith("✓") ? "#22c55e11" : "#DC262611", border: `1px solid ${msg.startsWith("✓") ? "#22c55e33" : "#DC262633"}`, borderRadius: 10, padding: "10px 14px", marginBottom: 14, fontSize: 13, color: msg.startsWith("✓") ? "#22c55e" : "#DC2626", display: "flex", justifyContent: "space-between" }}>
          <span>{msg}</span>
          <button onClick={() => setMsg("")} style={{ background: "none", border: "none", color: "inherit", cursor: "pointer" }}>×</button>
        </div>
      )}

      {showForm && isAdmin && (
        <div style={{ background: t.surface, border: `1px solid ${t.border}`, borderRadius: 16, padding: 22, marginBottom: 24 }}>
          <h4 style={{ margin: "0 0 16px", color: t.text, fontFamily: "Georgia, serif", fontWeight: 800 }}>New Report Schedule</h4>
          <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: 14 }}>
            <div>
              <label style={{ display: "block", fontSize: 10, color: t.textMuted, textTransform: "uppercase", letterSpacing: 1, marginBottom: 5, fontWeight: 700 }}>Schedule Name</label>
              <input value={form.name} onChange={e => setForm(p => ({ ...p, name: e.target.value }))} placeholder="e.g. Weekly Overview"
                style={{ width: "100%", background: t.surface2, border: `1px solid ${t.border}`, borderRadius: 8, padding: "9px 12px", color: t.text, fontSize: 13, outline: "none", boxSizing: "border-box" }} />
            </div>
            <div>
              <label style={{ display: "block", fontSize: 10, color: t.textMuted, textTransform: "uppercase", letterSpacing: 1, marginBottom: 5, fontWeight: 700 }}>Recipients (comma-separated)</label>
              <input value={form.recipients} onChange={e => setForm(p => ({ ...p, recipients: e.target.value }))} placeholder="user@alphamedia.co.zw, …"
                style={{ width: "100%", background: t.surface2, border: `1px solid ${t.border}`, borderRadius: 8, padding: "9px 12px", color: t.text, fontSize: 13, outline: "none", boxSizing: "border-box" }} />
            </div>
            {[
              { label: "Format", key: "format", options: FORMATS },
              { label: "Frequency", key: "frequency", options: FREQUENCIES },
            ].map(f => (
              <div key={f.key}>
                <label style={{ display: "block", fontSize: 10, color: t.textMuted, textTransform: "uppercase", letterSpacing: 1, marginBottom: 5, fontWeight: 700 }}>{f.label}</label>
                <select value={form[f.key]} onChange={e => setForm(p => ({ ...p, [f.key]: e.target.value }))}
                  style={{ width: "100%", background: t.surface2, border: `1px solid ${t.border}`, borderRadius: 8, padding: "9px 12px", color: t.text, fontSize: 13, cursor: "pointer" }}>
                  {f.options.map(o => <option key={o} value={o}>{o}</option>)}
                </select>
              </div>
            ))}
            <div style={{ gridColumn: isMobile ? "1" : "1 / -1" }}>
              <label style={{ display: "block", fontSize: 10, color: t.textMuted, textTransform: "uppercase", letterSpacing: 1, marginBottom: 5, fontWeight: 700 }}>Sections</label>
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                {SECTIONS.map(s => (
                  <label key={s} style={{ display: "flex", alignItems: "center", gap: 5, cursor: "pointer", fontSize: 12, color: t.textSub }}>
                    <input type="checkbox" checked={form.sections.includes(s)} onChange={e => setForm(p => ({ ...p, sections: e.target.checked ? [...p.sections, s] : p.sections.filter(x => x !== s) }))} style={{ accentColor: "#2563EB" }} />
                    {s}
                  </label>
                ))}
              </div>
            </div>
          </div>
          {formErr && <div style={{ color: "#DC2626", fontSize: 13, marginTop: 12 }}>⚠️ {formErr}</div>}
          <div style={{ display: "flex", gap: 10, marginTop: 16 }}>
            <button onClick={handleSave} disabled={saving}
              style={{ background: "#2563EB", border: "none", borderRadius: 8, padding: "9px 20px", color: "#fff", fontSize: 13, fontWeight: 800, cursor: saving ? "default" : "pointer", fontFamily: "Georgia, serif" }}>
              {saving ? "Creating…" : "Create Schedule"}
            </button>
            <button onClick={() => setShowForm(false)} style={{ background: "none", border: `1px solid ${t.border}`, borderRadius: 8, padding: "9px 16px", color: t.textSub, fontSize: 13, cursor: "pointer" }}>Cancel</button>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div style={{ display: "flex", gap: 4, marginBottom: 16, borderBottom: `1px solid ${t.border}`, paddingBottom: 10 }}>
        {[{ key: "schedules", label: `Schedules (${schedules.length})` }, { key: "history", label: `History (${history.length})` }].map(tb => (
          <button key={tb.key} onClick={() => setTab(tb.key)}
            style={{ background: tab === tb.key ? t.surface2 : "none", border: `1px solid ${tab === tb.key ? t.border2 : "transparent"}`, borderRadius: 8, padding: "6px 14px", color: tab === tb.key ? t.text : t.textMuted, fontSize: 12, cursor: "pointer", fontFamily: "Georgia, serif", fontWeight: tab === tb.key ? 800 : 500 }}>
            {tb.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div style={{ padding: "40px", textAlign: "center", color: t.textFaint }}>Loading…</div>
      ) : tab === "schedules" ? (
        schedules.length === 0 ? (
          <div style={{ padding: "60px 0", textAlign: "center" }}>
            <div style={{ fontSize: 36, marginBottom: 12 }}>📊</div>
            <div style={{ fontSize: 16, fontWeight: 700, color: t.text, fontFamily: "Georgia, serif" }}>No schedules yet</div>
            <div style={{ fontSize: 13, color: t.textMuted, marginTop: 4 }}>{isAdmin ? "Create your first report schedule above." : "No schedules configured."}</div>
          </div>
        ) : (
          <div style={{ background: t.surface, border: `1px solid ${t.border}`, borderRadius: 16, overflow: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12, minWidth: 500 }}>
              <thead>
                <tr style={{ borderBottom: `2px solid ${t.border}` }}>
                  {["Name", "Format", "Frequency", "Recipients", "Next Run", "Actions"].map(h => (
                    <th key={h} style={{ padding: "11px 14px", textAlign: "left", color: t.textMuted, fontWeight: 700, fontSize: 10, textTransform: "uppercase", letterSpacing: 0.8, whiteSpace: "nowrap" }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {schedules.map(s => (
                  <tr key={s.id} style={{ borderBottom: `1px solid ${t.border}` }} onMouseEnter={e => e.currentTarget.style.background = t.surface2} onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
                    <td style={{ padding: "11px 14px", color: t.text, fontWeight: 700 }}>{s.name}</td>
                    <td style={{ padding: "11px 14px", color: t.textSub, textTransform: "uppercase", fontSize: 11 }}>{s.format}</td>
                    <td style={{ padding: "11px 14px", color: t.textSub }}>{s.frequency}</td>
                    <td style={{ padding: "11px 14px", color: t.textFaint, fontSize: 11 }}>{(s.recipients || []).join(", ")}</td>
                    <td style={{ padding: "11px 14px", color: t.textFaint, fontSize: 11 }}>{s.nextRunAt ? new Date(s.nextRunAt).toLocaleDateString() : "—"}</td>
                    <td style={{ padding: "11px 14px" }}>
                      <div style={{ display: "flex", gap: 5 }}>
                        {isAdmin && <button onClick={() => handleRun(s.id)} style={{ background: "none", border: `1px solid ${t.border}`, borderRadius: 5, padding: "2px 8px", color: t.textSub, fontSize: 11, cursor: "pointer" }}>Run now</button>}
                        {isAdmin && <button onClick={() => handleDelete(s.id)} style={{ background: "none", border: "1px solid #DC262633", borderRadius: 5, padding: "2px 8px", color: "#DC2626", fontSize: 11, cursor: "pointer" }}>Delete</button>}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )
      ) : (
        history.length === 0 ? (
          <div style={{ padding: "60px 0", textAlign: "center", color: t.textFaint }}>No report history yet.</div>
        ) : (
          <div style={{ background: t.surface, border: `1px solid ${t.border}`, borderRadius: 16, overflow: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12, minWidth: 500 }}>
              <thead>
                <tr style={{ borderBottom: `2px solid ${t.border}` }}>
                  {["Schedule", "Format", "Status", "Started", "Recipients"].map(h => (
                    <th key={h} style={{ padding: "11px 14px", textAlign: "left", color: t.textMuted, fontWeight: 700, fontSize: 10, textTransform: "uppercase", letterSpacing: 0.8 }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {history.map(h => (
                  <tr key={h.id} style={{ borderBottom: `1px solid ${t.border}` }} onMouseEnter={e => e.currentTarget.style.background = t.surface2} onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
                    <td style={{ padding: "11px 14px", color: t.text, fontWeight: 600 }}>{h.scheduleName}</td>
                    <td style={{ padding: "11px 14px", color: t.textSub, textTransform: "uppercase", fontSize: 11 }}>{h.format}</td>
                    <td style={{ padding: "11px 14px" }}>
                      <span style={{ fontSize: 10, padding: "2px 8px", borderRadius: 10, background: `${STATUS_COLORS[h.status] || "#64748b"}18`, color: STATUS_COLORS[h.status] || "#64748b", border: `1px solid ${STATUS_COLORS[h.status] || "#64748b"}33`, fontWeight: 700 }}>
                        {h.status}
                      </span>
                    </td>
                    <td style={{ padding: "11px 14px", color: t.textFaint, fontSize: 11 }}>{h.startedAt ? new Date(h.startedAt).toLocaleString() : "—"}</td>
                    <td style={{ padding: "11px 14px", color: t.textFaint, fontSize: 11 }}>{(h.recipients || []).join(", ")}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )
      )}
    </div>
  );
}
